import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  type ActiveTemplateChoices,
  type TemplateChoiceSelection,
  templateChoicesFromMessage,
} from '@/components/case-partner/template-choices';
import { Loading } from '@/components/shared';
import { ReasoningSteps } from '@/components/ui/reasoning-steps';
import type { Message, QuickReplyQuestion, ThinkingStep } from '@/hooks/useChat';
import type { Document } from '@/hooks/useDocuments';
import { collectWebCitationMeta, type WebCitationDisplay } from '@/utils/citation-utils';
import {
  answerTextFromMessage,
  interleaveThinkingAndText,
  type TimelineTextInsertion,
  type TimelineWaitMarker,
} from './assistantTurnTimeline';
import { MessageItem } from './MessageItem';
import { QuickRepliesPanel, type QuickReplyAnswer, quickRepliesFromMessage } from './QuickReplies';
import { TemplateChoicesPanel } from './TemplateChoices';
import { TurnTimeline } from './TurnTimeline';

interface MessageStreamProps {
  messages: Message[];
  streamingContent: string;
  streamingQuickReplyQuestions?: QuickReplyQuestion[];
  streamingTemplateChoices?: ActiveTemplateChoices | null;
  streamingThinkingSteps: ThinkingStep[];
  streamingTextInsertions?: TimelineTextInsertion[];
  streamingWaitMarkers?: TimelineWaitMarker[];
  isStreaming: boolean;
  catchingUp?: boolean;
  finalAnswerConfirmed?: boolean;
  turnStartedAt?: number | null;
  reasoningDurationMs?: number | null;
  isThinking: boolean;
  /** Reveals the rest of the received answer at once instead of typing it out. */
  stopRequested?: boolean;
  isLoading?: boolean;
  showLoading?: boolean;
  documents: Document[] | undefined;
  matterGuid?: string;
  chatGuid?: string | null;
  slug: string;
  copiedMessageIds: Set<string>;
  onCopyMessage: (content: string, id: string | number | undefined) => void;
  onSaveToLibrary?: (content: string) => void;
  renderCitedDocs: (
    content: string,
    webCitationMeta?: ReadonlyMap<string, WebCitationDisplay>,
  ) => React.ReactNode;
  streamingContinuityKey?: string;
  variant?: 'full' | 'compact';
  quickReplyDisabled?: boolean;
  onQuickReplySubmit?: (value: string, answers: QuickReplyAnswer[]) => void;
  templateChoiceDisabled?: boolean;
  onTemplateChoiceSubmit?: (value: string, selection: TemplateChoiceSelection) => void;
  streamingResponseRef?: React.Ref<HTMLDivElement>;
}

interface WordExportMetadata {
  target?: 'self' | 'previous';
  assistant_response_offset?: number;
  filename?: string;
  include_citations?: boolean;
}

const getWordExportMetadata = (message: Message): WordExportMetadata | undefined => {
  const metadata = message.message_metadata?.word_export;
  if (!metadata || typeof metadata !== 'object') return undefined;
  return metadata as WordExportMetadata;
};

const getAssistantResponseOffset = (metadata: WordExportMetadata): number | undefined => {
  const offset = Number(metadata.assistant_response_offset);
  return Number.isInteger(offset) && offset > 0 ? offset : undefined;
};

const isWordExportConfirmation = (message: Message) => (
  getWordExportMetadata(message)?.target === 'previous'
);

export const MessageStream: React.FC<MessageStreamProps> = ({
  messages,
  streamingContent,
  streamingQuickReplyQuestions = [],
  streamingTemplateChoices = null,
  streamingThinkingSteps,
  streamingTextInsertions = [],
  streamingWaitMarkers = [],
  isStreaming,
  catchingUp = false,
  finalAnswerConfirmed = false,
  turnStartedAt = null,
  reasoningDurationMs = null,
  isThinking,
  stopRequested = false,
  isLoading = false,
  showLoading = true,
  documents,
  matterGuid,
  chatGuid,
  slug,
  copiedMessageIds,
  onCopyMessage,
  onSaveToLibrary,
  renderCitedDocs,
  streamingContinuityKey,
  variant = 'full',
  quickReplyDisabled = false,
  onQuickReplySubmit,
  templateChoiceDisabled = false,
  onTemplateChoiceSubmit,
  streamingResponseRef,
}) => {
  // Rebuilt on every keystroke of the stream otherwise, and the fresh item
  // identities defeat the memo on each MessageItem below it.
  const streamingTurnItems = useMemo(
    () => interleaveThinkingAndText(
      streamingThinkingSteps,
      streamingTextInsertions,
      streamingWaitMarkers,
    ),
    [streamingThinkingSteps, streamingTextInsertions, streamingWaitMarkers],
  );
  const streamingWebCitationMeta = useMemo(
    () => collectWebCitationMeta(streamingThinkingSteps),
    [streamingThinkingSteps],
  );
  const liveStreaming = isStreaming && !stopRequested;
  const [streamingSourcesReady, setStreamingSourcesReady] = useState(false);
  useEffect(() => {
    if (!stopRequested) setStreamingSourcesReady(false);
  }, [isStreaming, stopRequested]);
  const streamingCitedDocs = streamingSourcesReady
    ? renderCitedDocs(streamingContent, streamingWebCitationMeta)
    : null;
  const shouldShowStreamingTurn = isThinking || streamingTurnItems.length > 0 || (isStreaming && !streamingContent);
  // Catch-up snaps answer text that already exists. A turn that has only
  // started thinking has nothing to snap, so this chip stays live instead of
  // going blank until the next event.
  const pendingThinkingChip = (
    <ReasoningSteps
      thinkingSteps={streamingThinkingSteps}
      isStreaming={liveStreaming}
      revealImmediately={stopRequested}
      defaultExpanded={false}
    />
  );
  const shouldShowStreamingQuickReplies = (
    streamingQuickReplyQuestions.length > 0
    && Boolean(onQuickReplySubmit)
  );
  const shouldShowStreamingTemplateChoices = (
    Boolean(streamingTemplateChoices?.templates.length)
    && Boolean(onTemplateChoiceSubmit)
  );

  if (showLoading && isLoading && messages.length === 0 && !isStreaming) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loading />
      </div>
    );
  }

  const resolveWordExport = (message: Message, index: number) => {
    if (message.role !== 'assistant') return {};

    const metadata = getWordExportMetadata(message);
    if (!metadata) return {};

    const priorAssistantMessages = messages.slice(0, index).filter((candidate) => (
      candidate.role === 'assistant' && Boolean(candidate.content?.trim())
    ));
    const assistantResponseOffset = getAssistantResponseOffset(metadata);
    const targetMessage = metadata.target === 'previous'
      ? (
          assistantResponseOffset
            ? priorAssistantMessages[priorAssistantMessages.length - assistantResponseOffset]
            : [...priorAssistantMessages].reverse().find(candidate => !isWordExportConfirmation(candidate))
        )
      : message;
    const targetContent = targetMessage && answerTextFromMessage(targetMessage);

    if (!targetContent?.trim()) return {};

    return {
      wordExportContent: targetContent,
      wordExportFilename: metadata.filename,
      wordExportIncludeCitations: metadata.include_citations ?? true,
    };
  };

  const lastUserIndex = messages.reduce(
    (latestIndex, message, index) => (message.role === 'user' ? index : latestIndex),
    -1,
  );
  let activeQuickReplyIndex = -1;
  let activeQuickReplies: ReturnType<typeof quickRepliesFromMessage> = null;
  let activeTemplateChoiceIndex = -1;
  let activeTemplateChoices: ReturnType<typeof templateChoicesFromMessage> = null;

  if (onQuickReplySubmit) {
    for (let index = messages.length - 1; index > lastUserIndex; index -= 1) {
      const message = messages[index];
      if (message.role !== 'assistant') continue;

      const quickReplies = quickRepliesFromMessage(message);
      if (quickReplies) {
        activeQuickReplyIndex = index;
        activeQuickReplies = quickReplies;
        break;
      }
    }
  }

  if (onTemplateChoiceSubmit) {
    for (let index = messages.length - 1; index > lastUserIndex; index -= 1) {
      const message = messages[index];
      if (message.role !== 'assistant') continue;

      const templateChoices = templateChoicesFromMessage(message);
      if (templateChoices) {
        activeTemplateChoiceIndex = index;
        activeTemplateChoices = templateChoices;
        break;
      }
    }
  }

  return (
    <>
      {messages.map((message, index) => (
        <MessageItem
          key={message.id || message.timestamp.getTime()}
          message={message}
          documents={documents}
          matterGuid={matterGuid}
          chatGuid={chatGuid}
          slug={slug}
          isCopied={copiedMessageIds.has(message.id?.toString() || '')}
          onCopy={onCopyMessage}
          onSaveToLibrary={onSaveToLibrary}
          renderCitedDocs={message.role === 'assistant' ? renderCitedDocs : undefined}
          draftCardsDefaultExpanded={Boolean(message.draftCardsDefaultExpanded)}
          quickReplyQuestions={index === activeQuickReplyIndex ? activeQuickReplies?.questions : undefined}
          quickReplyDisabled={quickReplyDisabled}
          onQuickReplySubmit={onQuickReplySubmit}
          templateChoices={index === activeTemplateChoiceIndex ? activeTemplateChoices : undefined}
          templateChoiceDisabled={templateChoiceDisabled}
          onTemplateChoiceSubmit={onTemplateChoiceSubmit}
          variant={variant}
          {...resolveWordExport(message, index)}
        />
      ))}

      {shouldShowStreamingTurn && (
        variant === 'compact' ? (
          <div>
            <div ref={streamingResponseRef} className="px-4">
              {streamingTurnItems.length > 0 ? (
                <TurnTimeline
                  documents={documents}
                  finalAnswerConfirmed={finalAnswerConfirmed}
                  isStreaming={liveStreaming}
                  catchingUp={catchingUp}
                  items={streamingTurnItems}
                  onSourcesReady={setStreamingSourcesReady}
                  slug={slug}
                  stopRequested={stopRequested}
                  streamingContinuityKey={streamingContinuityKey}
                  turnStartedAt={turnStartedAt}
                  reasoningDurationMs={reasoningDurationMs}
                  webCitationMeta={streamingWebCitationMeta}
                />
              ) : (
                pendingThinkingChip
              )}
              {shouldShowStreamingTemplateChoices && onTemplateChoiceSubmit && streamingTemplateChoices && (
                <div className="mt-5 mb-5">
                  <TemplateChoicesPanel
                    allowNone={streamingTemplateChoices.allowNone}
                    disabled={templateChoiceDisabled}
                    matterGuid={matterGuid}
                    onSubmit={onTemplateChoiceSubmit}
                    question={streamingTemplateChoices.question}
                    templates={streamingTemplateChoices.templates}
                  />
                </div>
              )}
              {shouldShowStreamingQuickReplies && onQuickReplySubmit && (
                <div className="mt-5 mb-5">
                  <QuickRepliesPanel
                    disabled={quickReplyDisabled}
                    onSubmit={onQuickReplySubmit}
                    questions={streamingQuickReplyQuestions}
                  />
                </div>
              )}
              {streamingCitedDocs && (
                <div className="case-partner-cited-docs">
                  {streamingCitedDocs}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-start justify-start">
              <div
                className="max-w-full rounded-2xl bg-transparent px-4 py-3"
                ref={streamingResponseRef}
              >
                {streamingTurnItems.length > 0 ? (
                  <TurnTimeline
                    documents={documents}
                    finalAnswerConfirmed={finalAnswerConfirmed}
                    isStreaming={liveStreaming}
                    catchingUp={catchingUp}
                    items={streamingTurnItems}
                    onSourcesReady={setStreamingSourcesReady}
                    slug={slug}
                    stopRequested={stopRequested}
                    streamingContinuityKey={streamingContinuityKey}
                    turnStartedAt={turnStartedAt}
                    reasoningDurationMs={reasoningDurationMs}
                    webCitationMeta={streamingWebCitationMeta}
                  />
                ) : (
                  pendingThinkingChip
                )}
                {shouldShowStreamingTemplateChoices && onTemplateChoiceSubmit && streamingTemplateChoices && (
                  <div className="mt-5 mb-5">
                    <TemplateChoicesPanel
                      allowNone={streamingTemplateChoices.allowNone}
                      disabled={templateChoiceDisabled}
                      matterGuid={matterGuid}
                      onSubmit={onTemplateChoiceSubmit}
                      question={streamingTemplateChoices.question}
                      templates={streamingTemplateChoices.templates}
                    />
                  </div>
                )}
                {shouldShowStreamingQuickReplies && onQuickReplySubmit && (
                  <div className="mt-5 mb-5">
                    <QuickRepliesPanel
                      disabled={quickReplyDisabled}
                      onSubmit={onQuickReplySubmit}
                      questions={streamingQuickReplyQuestions}
                    />
                  </div>
                )}
                {streamingCitedDocs && (
                  <div className="case-partner-cited-docs">
                    {streamingCitedDocs}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      )}

      {/* Gives a newly submitted response room to grow without following it. */}
      {isStreaming
        ? <div aria-hidden="true" className="h-[30vh] min-h-32 max-h-64 shrink-0" />
        : null}
    </>
  );
};

export default MessageStream;
